import {
  findTransportationProfiles,
  findTotalTransportationProfiles
} from '../../services/transportation-profiles-service'
import { generateKeywordsQueryForOr } from '@smarttransit/common'
import { addAlert } from '@/utilities/helpers'

export default {
  name: 'transportation-profile-selector',
  props: {
    initialSelectedProfileIds: {
      type: Array,
      default () {
        return []
      }
    },
    agencyId: {
      type: String,
      default: null
    },
    initialAgencyId: {
      type: String,
      default: null
    },
    resultLimit: {
      type: Number,
      default: 20
    },
    searchDelay: {
      type: Number,
      default: 800
    },
    labelSelected: {
      type: String,
      default: 'Selected vehicles'
    },
    labelSearch: {
      type: String,
      default: 'Search & add vehicles'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedProfiles: [],
      searchKeywords: '',
      selectedAgencyId: null,
      searchResults: [],
      apiInProgress: false,
      totalCount: 0,
      currentPage: 1,
      searchKeywordsHandle: null,
      agencyOptions: []
    }
  },
  computed: {
    limit () {
      return this.resultLimit || 20
    },
    totalPages () {
      if (!this.totalCount || this.totalCount <= 0) {
        return 1
      }
      const pages = Math.ceil(this.totalCount / this.limit)
      return pages > 0 ? pages : 1
    },
    fromItem () {
      if (!this.totalCount) {
        return 0
      }
      return (this.currentPage - 1) * this.limit + 1
    },
    toItem () {
      if (!this.totalCount) {
        return 0
      }
      const tentative = this.currentPage * this.limit
      return tentative > this.totalCount ? this.totalCount : tentative
    },
    hasAgencyDropdown () {
      return Array.isArray(this.agencyOptions) && this.agencyOptions.length > 0
    },
    selectedProfileIds () {
      return this.selectedProfiles.map(function (p) { return p.id })
    }
  },
  watch: {
    initialSelectedProfileIds () {
      this.loadInitialSelectedProfiles()
    },
    searchKeywords (val) {
      if (this.disabled) {
        return
      }

      clearTimeout(this.searchKeywordsHandle)

      if (!val) {
        this.currentPage = 1
        // If another filter (e.g. agency) is active, refresh results using that filter only.
        if (this.selectedAgencyId || this.agencyId) {
          this.performSearch()
        } else {
          this.searchResults = []
          this.totalCount = 0
        }
        return
      }

      this.currentPage = 1

      const self = this
      this.searchKeywordsHandle = setTimeout(function () {
        self.performSearch()
      }, this.searchDelay || 800)
    },
    selectedAgencyId () {
      if (this.disabled) {
        return
      }

      this.currentPage = 1
      this.performSearch()
    },
    currentPage (val, oldVal) {
      if (this.disabled) {
        return
      }

      if (val === oldVal) {
        return
      }

      if (val < 1) {
        this.currentPage = 1
        return
      }

      if (val > this.totalPages) {
        this.currentPage = this.totalPages
        return
      }

      this.performSearch()
    }
  },
  async mounted () {
    this.initSelectedAgency()
    await this.loadInitialSelectedProfiles()
    this.agencyOptions = this.$store.getters.getAgencies

    // If agency is preselected or an initial keyword is provided in future,
    // we can load an initial page of results.
    if (this.selectedAgencyId) {
      this.performSearch()
    }
  },
  beforeDestroy () {
    clearTimeout(this.searchKeywordsHandle)
  },
  methods: {
    initSelectedAgency () {
      if (this.initialAgencyId) {
        this.selectedAgencyId = this.initialAgencyId
      } else if (this.agencyId) {
        this.selectedAgencyId = this.agencyId
      } else if (this.hasAgencyDropdown && this.agencyOptions[0] && this.agencyOptions[0].value) {
        this.selectedAgencyId = this.agencyOptions[0].value
      } else {
        this.selectedAgencyId = null
      }
    },
    async loadInitialSelectedProfiles () {
      if (!Array.isArray(this.initialSelectedProfileIds) || !this.initialSelectedProfileIds.length) {
        return
      }

      try {
        const filter = {
          where: {
            id: { inq: this.initialSelectedProfileIds }
          },
          limit: this.initialSelectedProfileIds.length,
          order: 'dateCreated DESC'
        }

        const results = await findTransportationProfiles(filter)
        const profiles = Array.isArray(results) ? results : []

        // Preserve initial order of IDs where possible
        const idOrder = {}
        this.initialSelectedProfileIds.forEach(function (id, index) {
          idOrder[id] = index
        })

        profiles.sort(function (a, b) {
          const aIndex = idOrder[a.id]
          const bIndex = idOrder[b.id]

          if (aIndex == null && bIndex == null) {
            return 0
          }
          if (aIndex == null) {
            return 1
          }
          if (bIndex == null) {
            return -1
          }
          return aIndex - bIndex
        })

        this.selectedProfiles = profiles
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      }
    },
    buildWhereFilter () {
      const where = {}

      if (this.selectedAgencyId) {
        where.agencyId = this.selectedAgencyId
      } else if (this.agencyId) {
        where.agencyId = this.agencyId
      }

      if (this.searchKeywords) {
        const orQuery = generateKeywordsQueryForOr(this.searchKeywords, ['licensePlateNumber', 'shortcodeId'])
        if (orQuery && orQuery.length) {
          where.or = orQuery
        }
      }

      return where
    },
    isProfileSelected (profileId) {
      if (!profileId) {
        return false
      }
      return this.selectedProfileIds.indexOf(profileId) !== -1
    },
    addProfile (profile) {
      if (this.disabled || !profile || !profile.id) {
        return
      }

      if (this.isProfileSelected(profile.id)) {
        return
      }

      this.selectedProfiles = this.selectedProfiles.concat([profile])
    },
    removeProfile (profileId) {
      if (this.disabled || !profileId) {
        return
      }

      this.selectedProfiles = this.selectedProfiles.filter(function (p) {
        return p.id !== profileId
      })
    },
    goToPreviousPage () {
      if (this.currentPage <= 1) {
        return
      }
      this.currentPage = this.currentPage - 1
    },
    goToNextPage () {
      if (this.currentPage >= this.totalPages) {
        return
      }
      this.currentPage = this.currentPage + 1
    },
    async performSearch () {
      if (this.disabled) {
        return
      }

      const where = this.buildWhereFilter()

      // If there is no filter at all, avoid loading all profiles unintentionally.
      if (!Object.keys(where).length) {
        this.searchResults = []
        this.totalCount = 0
        return
      }

      const limit = this.limit
      const skip = (this.currentPage - 1) * limit

      const filter = {
        where: where,
        limit: limit,
        skip: skip,
        order: 'dateCreated DESC'
      }

      this.apiInProgress = true
      try {
        const whereForCount = Object.keys(where).length ? where : undefined

        const [results, total] = await Promise.all([
          findTransportationProfiles(filter),
          findTotalTransportationProfiles({ where: whereForCount })
        ])

        this.searchResults = Array.isArray(results) ? results : []

        if (total && typeof total.count === 'number') {
          this.totalCount = total.count
        } else if (Array.isArray(results)) {
          this.totalCount = results.length
        } else {
          this.totalCount = 0
        }
      } catch (err) {
        this.searchResults = []
        this.totalCount = 0
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    onSubmit () {
      if (this.disabled) {
        return
      }

      this.$emit('submit', {
        ids: this.selectedProfileIds.slice(),
        profiles: this.selectedProfiles.slice()
      })
    },
    onCancel () {
      this.$emit('cancel')
    }
  }
}
